import { api } from "../lib/api";
import { localISODate, resolveTimeZone } from "../lib/dates";
import { useAuth } from "../lib/auth";
import { useApiData } from "../lib/hooks";
import { ErrorMessage } from "../components/ErrorMessage";
import { RoundSummaryCard } from "../components/RoundSummaryCard";
import { EmptyState } from "../components/EmptyState";

// How many recent sessions to show as cards. The enriched list response
// (#1310) carries everything a card needs (shot_positions + ring counts), so
// no per-card detail fetch is made — but the list stays modest regardless.
//
// This is a DISPLAY limit and nothing else. It used to bound the "This month"
// counter too, because the counter filtered this same twelve-row page — see
// the note on the count fetch below (#2211).
const SESSION_LIMIT = 12;

// Best/avg across the LOADED sessions — the recent-form read the "Recent
// sessions" list beneath them is showing, which is why they are page-scoped on
// purpose and "This month" (a population count) is not.
function sessionAggregate(rounds) {
  if (rounds.length === 0) return null;
  const totals = rounds.map((r) => r.total ?? 0);
  const best = Math.max(...totals);
  const avg = Math.round(totals.reduce((a, b) => a + b, 0) / totals.length);
  return { best, avg };
}

export function Practice() {
  const { activeArcher } = useAuth();

  // include=thumbnails opts back into shot_positions (#2055) — with the ring
  // counts (always on) that is everything the card needs, so the cards render
  // directly from the list — no per-card detail fetch (#1310).
  // NO `active` param. The backend documents it as "True = in-progress;
  // False = completed" and omitting it means BOTH — so `active: false` asked
  // for completed rounds only. #2198 removed the `if (!r.completed_at) return
  // false` gate from sessionAggregate so in-progress rounds would count, but
  // the fetch upstream of it had already filtered every one of them out, so
  // nothing changed on screen and the counter still under-reported (#2208).
  // The two halves of that gate have to come off together.
  const { data, error } = useApiData(
    () => api.rounds({ limit: SESSION_LIMIT, include: "thumbnails" }),
    [],
  );
  const sessions = data ?? [];

  // "This month" is COUNTED BY THE SERVER (#2211).
  //
  // It used to be `rounds.filter(...).length` over the twelve-row page above,
  // so twelve was the highest number it could ever show — anyone shooting ~3x a
  // week outgrows that inside a month, and the counter just quietly stopped
  // going up. Raising SESSION_LIMIT only moves the ceiling; a count of a
  // population cannot be derived from a page of it, at any page size.
  //
  // The month is the one the archer is STANDING IN, from `localISODate` (the
  // local-day SSOT of #2160) — never `toISOString()`, which renders in UTC and
  // east of Greenwich reports the previous day, and on the 1st the previous
  // MONTH. The zone goes with it because the server cannot see the browser's:
  // `resolveTimeZone` is the same archer-tz-then-browser fallback every date on
  // this page is rendered through, so the counter and the cards below it can
  // never be measuring two different calendars.
  //
  // The server does NOT reuse the existing `date_from` filter to do this — that
  // comparison is timezone-naive (see app/timeutil.month_utc_window), and this
  // counter has already shipped three month-boundary bugs (#2031, #2160,
  // #2185). Nor does it read `completed_at`: the date of a round is
  // `started_at` (#2047), and in-progress rounds count (#2185, #2208).
  const month = localISODate().slice(0, 7);
  const tz = resolveTimeZone(activeArcher?.timezone);
  const { data: monthCount } = useApiData(
    () => api.roundsMonthCount({ month, tz }),
    [month, tz],
  );

  const agg = sessionAggregate(sessions);

  return (
    <main className="main">
      <div className="main-head">
        <div>
          <h1>Practice</h1>
          <div className="greeting">Sessions logged off-the-line.</div>
        </div>
      </div>

      {agg && (
        <div className="panel u-mb-16">
          <div className="stats-row">
            <div className="stat">
              <div className="stat-label">This month</div>
              {/* An em dash while the count is in flight or its request
                  failed. A stale or invented number here is exactly the
                  failure #2211 is about, so show nothing rather than the
                  page size. */}
              <div className="stat-value">{monthCount?.total ?? "—"}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Best session</div>
              <div className="stat-value">{agg.best}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Avg total</div>
              <div className="stat-value">{agg.avg}</div>
            </div>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">Recent sessions</div>
        </div>
        <div className="rsc-list">
          {/* Previously `.catch(() => {})` — a failed load was indistinguishable
              from having no sessions yet, so the page told you to go shoot one
              (#2068). useApiData surfaces the error instead. */}
          {error && <ErrorMessage message={error} />}
          {!error && sessions.length === 0 && (
            <EmptyState variant="inline" message="Log your first practice round from the Home screen." />
          )}
          {sessions.map((r) => (
            <RoundSummaryCard key={r.id} round={r} tz={activeArcher?.timezone} />
          ))}
        </div>
      </div>
    </main>
  );
}
